import React, { useState, useCallback } from "react";
import {
    AddIcon,
    ButtonWrapperDiv,
    CloneIcon,
    DeleteIcon,
    EditIcon,
    EditOffIcon,
    PathwayIconButton,
    PathwayIconWrapperButton,
    SaveIcon,
    SettingsIcon,
    SpinnerIcon,
} from "../StyledComponents/PathwayIcons";
import {
    flip,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
} from "@floating-ui/react";
import PropTypes from "prop-types";
import stringUtils from "../../_services/string-utils";
import styled from "styled-components";
import { tooltipSurface } from "../StyledComponents/tooltipSurface";

const FLOATING_MIDDLEWARE = [offset(6), flip(), shift({ padding: 8 })];

// The tooltip's wrapper stays display:contents so it adds nothing to the layout — but that
// also means it has no box of its own, and measuring it returns all zeros, which parks the
// tooltip in the page corner instead of next to the button. Position against the button,
// which sits one or two wrappers deep depending on which button component is tooltipped.
export const measurableReference = (node) => {
    if (!node) {
        return null;
    }
    return node.querySelector("button") || node.firstElementChild || node;
};

/** Inline tooltip attached to a button via floating-ui (replaces reactstrap Tooltip). */
const ButtonTooltip = ({ tooltip, tooltipWidth, tooltipPlacement = "right", children }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: tooltipPlacement,
        middleware: FLOATING_MIDDLEWARE,
    });

    const hover = useHover(context, { delay: { open: 120, close: 0 } });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    const setReference = useCallback(
        (node) => {
            refs.setReference(node);
            refs.setPositionReference(measurableReference(node));
        },
        [refs]
    );

    return (
        <>
            <span ref={setReference} style={{ display: "contents" }} {...getReferenceProps()}>
                {children}
            </span>
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={{
                            ...tooltipSurface(tooltipWidth),
                            padding: "4px 10px",
                            zIndex: 9999,
                            pointerEvents: "none",
                            ...floatingStyles,
                        }}
                        {...getFloatingProps()}
                    >
                        {tooltip}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

const iconButtonPropTypes = {
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    dataCy: PropTypes.string,
    iconSize: PropTypes.string,
};

// Chrome (fill, radius, weight, press nudge) lives in layout/scss/_buttons.scss.
// This adds only what a styled wrapper is needed for on top of those base classes.
const btnClassName = (color, outline, size, extra) => {
    const colorPart = outline ? `btn-outline-${color || "primary"}` : `btn-${color || "primary"}`;
    return ["btn", colorPart, size ? `btn-${size}` : null, extra].filter(Boolean).join(" ");
};

const StyledButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
`;

export const PathwayDeleteButton = ({ onClick, disabled, dataCy, iconSize, style, boxed = true }) => {
    if (boxed) {
        return (
            <PathwayIconButton
                type="button"
                disabled={disabled}
                onClick={onClick}
                data-cy={dataCy}
                style={style}
                className="pw-icon-danger"
                aria-label="Löschen"
            >
                <i className="fa fa-trash" aria-hidden="true" style={{ fontSize: "13px" }} />
            </PathwayIconButton>
        );
    }
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy} style={style}>
            <DeleteIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayDeleteButton.propTypes = {
    ...iconButtonPropTypes,
    boxed: PropTypes.bool,
};

export const PathwayCloneButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton
            disabled={disabled}
            onClick={onClick}
            data-cy={dataCy}
            aria-label="Konfiguration klonen"
        >
            <CloneIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayCloneButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayRefreshButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconButton
            type="button"
            disabled={disabled}
            onClick={onClick}
            data-cy={dataCy}
            className="pw-icon-brand"
            aria-label="Neu verarbeiten"
        >
            <i className="fa-solid fa-rotate-right" aria-hidden="true" style={{ fontSize: "13px" }} />
        </PathwayIconButton>
    );
};

PathwayRefreshButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayDownloadButton = ({ onClick, disabled, dataCy }) => {
    return (
        <PathwayIconButton
            type="button"
            disabled={disabled}
            onClick={onClick}
            data-cy={dataCy}
            className="pw-icon-brand"
            aria-label="Herunterladen"
        >
            <i className="fa-solid fa-download" aria-hidden="true" style={{ fontSize: "13px" }} />
        </PathwayIconButton>
    );
};

PathwayDownloadButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayAddButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <AddIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayAddButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwayEditButton = ({ onClick, disabled, dataCy, iconSize, boxed = true }) => {
    if (boxed) {
        return (
            <PathwayIconButton
                type="button"
                disabled={disabled}
                onClick={onClick}
                data-cy={dataCy}
                className="pw-icon-brand"
                aria-label="Bearbeiten"
            >
                <i className="fa fa-pen" aria-hidden="true" style={{ fontSize: "13px" }} />
            </PathwayIconButton>
        );
    }
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <EditIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayEditButton.propTypes = {
    ...iconButtonPropTypes,
    boxed: PropTypes.bool,
};

export const PathwayEditOffButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <EditOffIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwayEditOffButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwaySettingsButton = ({ onClick, disabled, dataCy, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <SettingsIcon size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwaySettingsButton.propTypes = {
    ...iconButtonPropTypes,
};

export const PathwaySaveButton = ({ onClick, disabled, dataCy, color, iconSize }) => {
    return (
        <PathwayIconWrapperButton disabled={disabled} onClick={onClick} data-cy={dataCy}>
            <SaveIcon color={color} size={iconSize} />
        </PathwayIconWrapperButton>
    );
};

PathwaySaveButton.propTypes = {
    ...iconButtonPropTypes,
    color: PropTypes.string,
};

export const PathwayButton = ({
    children,
    onClick,
    type,
    color,
    size,
    outline,
    disabled,
    dataCy,
    id,
    style,
    className,
    title,
    tooltip,
    tooltipWidth,
    tooltipPlacement = "right",
    fontColor,
    ariaLabel,
    ariaPressed,
    ariaExpanded,
    ariaControls,
}) => {
    const buttonType = ["button", "submit", "reset"].includes(type) ? type : "button";
    const buttonNode = (
        <ButtonWrapperDiv>
            <StyledButton
                type={buttonType}
                id={id}
                data-cy={dataCy}
                disabled={disabled}
                onClick={onClick}
                style={{ color: fontColor, ...style }}
                className={btnClassName(color, outline, size, className)}
                title={title}
                aria-label={ariaLabel}
                aria-pressed={ariaPressed}
                aria-expanded={ariaExpanded}
                aria-controls={ariaControls}
            >
                {children}
            </StyledButton>
        </ButtonWrapperDiv>
    );

    if (!stringUtils.isEmpty(tooltip)) {
        return (
            <ButtonTooltip tooltip={tooltip} tooltipWidth={tooltipWidth} tooltipPlacement={tooltipPlacement}>
                {buttonNode}
            </ButtonTooltip>
        );
    }
    return buttonNode;
};
PathwayButton.propTypes = {
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func,
    type: PropTypes.oneOf(["button", "submit", "reset"]),
    color: PropTypes.string,
    size: PropTypes.string,
    outline: PropTypes.bool,
    disabled: PropTypes.bool,
    dataCy: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    title: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    fontColor: PropTypes.string,
    ariaLabel: PropTypes.string,
    id: PropTypes.string,
    ariaPressed: PropTypes.bool,
    // A button that opens a panel has to say so and say what it opens, or the panel is invisible to a screen
    // reader. Forwarded here so no caller has to reach past the component to set them.
    ariaExpanded: PropTypes.bool,
    ariaControls: PropTypes.string,
};

export const PathwayLinkButton = ({ children, onClick, disabled, dataCy, style, className, title }) => {
    return (
        <StyledButton
            type="button"
            data-cy={dataCy}
            disabled={disabled}
            onClick={(e) => onClick(e)}
            style={style}
            className={["btn", "btn-link", className].filter(Boolean).join(" ")}
            title={title}
        >
            {children}
        </StyledButton>
    );
};

PathwayLinkButton.propTypes = {
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    dataCy: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
    title: PropTypes.string,
};

export const LoadingSpinnerButton = ({
    name,
    type,
    loading,
    children,
    onClick,
    disabled,
    color,
    buttonCyData,
    size,
    tooltip,
    tooltipWidth,
    tooltipPlacement = "right",
    style,
}) => {
    const buttonNode = (
        <ButtonWrapperDiv style={style}>
            <StyledButton
                name={name}
                type={type || "button"}
                data-cy={buttonCyData}
                disabled={loading || !!disabled}
                onClick={onClick}
                className={btnClassName(color, false, null, null)}
            >
                {loading ? (
                    <LoadingContent>
                        <HiddenLabel>{children}</HiddenLabel>
                        <CenteredSpinner data-cy={buttonCyData} $size={size ? size : "1.15rem"} />
                    </LoadingContent>
                ) : (
                    children
                )}
            </StyledButton>
        </ButtonWrapperDiv>
    );

    if (!stringUtils.isEmpty(tooltip)) {
        return (
            <ButtonTooltip tooltip={tooltip} tooltipWidth={tooltipWidth} tooltipPlacement={tooltipPlacement}>
                {buttonNode}
            </ButtonTooltip>
        );
    }
    return buttonNode;
};
LoadingSpinnerButton.propTypes = {
    // Not required: a type="submit" button inside a form is driven by the form, not by a handler. Marking it
    // required logged a propType error on every render of ReportBugModal.
    type: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    children: PropTypes.any.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    buttonCyData: PropTypes.string,
    size: PropTypes.string,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    tooltipWidth: PropTypes.string,
    tooltipPlacement: PropTypes.string,
};

export const LoadingSpinnerDropdownButton = ({
    loading,
    children,
    items,
    disabled,
    color,
    tooltip,
    tooltipWidth,
    tooltipPlacement,
    buttonCyData,
    size,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isDropdownOpen,
        onOpenChange: setIsDropdownOpen,
        placement: "bottom-start",
        middleware: FLOATING_MIDDLEWARE,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    const toggle = (
        <ButtonWrapperDiv>
            <DropdownStyledButton
                ref={refs.setReference}
                type="button"
                data-cy={buttonCyData}
                disabled={loading || disabled}
                className={btnClassName(color || "primary", false, null, null)}
                style={{ borderRadius: "var(--pw-radius-md)" }}
                {...getReferenceProps()}
            >
                {loading ? (
                    <LoadingContent>
                        <HiddenLabel>{children}</HiddenLabel>
                        <CenteredSpinner data-cy={buttonCyData} $size={size ? size : "1.15rem"} />
                    </LoadingContent>
                ) : (
                    <>
                        {children}
                        <DropdownChevron $open={isDropdownOpen} className="fa-solid fa-chevron-down" aria-hidden="true" />
                    </>
                )}
            </DropdownStyledButton>
        </ButtonWrapperDiv>
    );

    const menu = isDropdownOpen && (
        <FloatingPortal>
            <div
                ref={refs.setFloating}
                className="dropdown-menu show"
                style={{ ...floatingStyles, zIndex: 1050 }}
                {...getFloatingProps()}
            >
                {items.map((entry, index) => {
                    if (entry === null) {
                        return <hr key={index} className="dropdown-divider" style={{ margin: "0" }} />;
                    }
                    // The reactstrap DropdownItem this replaced forwarded the event and skipped a missing
                    // handler, so call sites rely on both.
                    return (
                        <button
                            key={index}
                            type="button"
                            className="dropdown-item"
                            disabled={entry[2]}
                            onClick={(event) => {
                                setIsDropdownOpen(false);
                                if (typeof entry[1] === "function") {
                                    entry[1](event);
                                }
                            }}
                            data-cy={entry[3] || undefined}
                        >
                            {entry[0]}
                        </button>
                    );
                })}
            </div>
        </FloatingPortal>
    );

    if (!stringUtils.isEmpty(tooltip)) {
        return (
            <ButtonTooltip tooltip={tooltip} tooltipWidth={tooltipWidth} tooltipPlacement={tooltipPlacement}>
                {toggle}
                {menu}
            </ButtonTooltip>
        );
    }
    return (
        <>
            {toggle}
            {menu}
        </>
    );
};

LoadingSpinnerDropdownButton.propTypes = {
    loading: PropTypes.bool.isRequired,
    children: PropTypes.any.isRequired,
    items: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    buttonCyData: PropTypes.string,
    size: PropTypes.string,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    tooltipWidth: PropTypes.string,
    tooltipPlacement: PropTypes.string,
};

// A button must not resize while it works: replacing its label with a spinner made it both shorter and
// narrower mid-request. The label keeps holding the space and only turns invisible; the spinner sits over it.
const LoadingContent = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const HiddenLabel = styled.span`
    visibility: hidden;
`;

const CenteredSpinner = styled(SpinnerIcon)`
    position: absolute;
    inset: 0;
    margin: auto;
`;

// position:relative + extra right padding so the absolutely-placed chevron
// doesn't overlap the (still-centered) label text.
const DropdownStyledButton = styled(StyledButton)`
    position: relative;
    padding-right: 2rem;
    min-width: 8rem;
`;

const DropdownChevron = styled.i`
    position: absolute;
    right: 0.75rem;
    font-size: 0.75em;
    transition: transform 0.15s ease;
    transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
`;
